import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Upload, Sparkles, FileText } from "lucide-react";

const FAMILIES = [
  "PRAMAANA Constitution", "Decision Ledger", "Open Questions", "Project Ayodhya Architecture",
  "Project Ayodhya Engineering Record", "Quiet MVP / Pilot Strategy", "Financial Transparency Discussions",
  "Reliance / Ambani Strategy", "PRAMAANA Pitch Documents", "Founder Public Writing",
  "Raw AI Conversations", "External Research", "Founder Corrections",
];

export default function Sources() {
  const [sources, setSources] = useState([]);
  const [open, setOpen] = useState(false);
  const [extracting, setExtracting] = useState(null);

  const load = useCallback(() => api.get("/sources").then((r) => setSources(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const extract = async (id) => {
    setExtracting(id);
    try {
      const { data } = await api.post(`/sources/${id}/extract-claims`);
      toast.success(`${data.created} candidate claim(s) extracted. Review them in Claim Review.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extraction failed");
    } finally {
      setExtracting(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Sources"
        subtitle="Evidence, not automatically authoritative knowledge. Import text, notes, or documents — then extract candidate claims for founder review."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2" data-testid="add-source-btn"><Plus size={16} /> Import Source</Button>
            </DialogTrigger>
            <SourceForm onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />
      <div className="p-10">
        <div className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="sources-list">
          {sources.length === 0 && <div className="p-8 text-slate-400 text-sm">No sources imported yet.</div>}
          {sources.map((s) => (
            <div key={s.id} className="px-6 py-5 flex items-start gap-4" data-testid={`source-${s.id}`}>
              <FileText size={18} className="text-slate-400 mt-1 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-slate-500 bg-slate-100 px-1.5 rounded-sm">{s.source_identifier}</span>
                  <h3 className="font-semibold text-slate-800">{s.title}</h3>
                  {s.is_founder_authored && <span className="font-mono text-[10px] uppercase bg-slate-900 text-white px-1.5 py-0.5 rounded-sm">Founder authored</span>}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 font-mono">
                  <span>{s.source_family}</span><span>·</span><span>{s.source_type}</span><span>·</span><span>authority {s.authority_level}</span>
                </div>
                <p className="text-sm text-slate-500 mt-2 line-clamp-2">{(s.raw_text || "").slice(0, 240)}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => extract(s.id)}
                disabled={extracting === s.id}
                className="rounded-sm gap-1.5 shrink-0"
                data-testid={`extract-claims-${s.id}`}
              >
                <Sparkles size={14} /> {extracting === s.id ? "Extracting…" : "Extract Claims"}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SourceForm({ onDone }) {
  const [tab, setTab] = useState("text");
  const [title, setTitle] = useState("");
  const [family, setFamily] = useState("");
  const [type, setType] = useState("PASTED_TEXT");
  const [raw, setRaw] = useState("");
  const [founder, setFounder] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim() || !family) { toast.error("Title and source family are required."); return; }
    setBusy(true);
    try {
      if (tab === "file") {
        if (!file) { toast.error("Choose a file."); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title);
        fd.append("source_family", family);
        fd.append("is_founder_authored", founder);
        await api.post("/sources/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/sources", { title, source_family: family, source_type: type, raw_text: raw, is_founder_authored: founder });
      }
      toast.success("Source imported.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="max-w-xl rounded-sm bg-white" data-testid="source-form">
      <DialogHeader><DialogTitle className="font-serif text-2xl">Import Source</DialogTitle></DialogHeader>
      <div className="flex gap-2 mb-2">
        {[["text", "Text / Note"], ["file", "Upload Document"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`source-tab-${k}`}
            className={`px-3 py-1.5 text-sm border rounded-sm ${tab === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-4">
        <div>
          <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 rounded-sm border-slate-300" data-testid="source-title" />
        </div>
        <div>
          <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Source Family</Label>
          <Select value={family} onValueChange={setFamily}>
            <SelectTrigger className="mt-1.5 rounded-sm border-slate-300" data-testid="source-family"><SelectValue placeholder="Select family" /></SelectTrigger>
            <SelectContent className="bg-white max-h-64">{FAMILIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {tab === "text" ? (
          <>
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1.5 rounded-sm border-slate-300" data-testid="source-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="PASTED_TEXT">Pasted Text</SelectItem>
                  <SelectItem value="FOUNDER_NOTE">Founder Note</SelectItem>
                  <SelectItem value="DOCUMENT">Document Text</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Content</Label>
              <Textarea value={raw} onChange={(e) => setRaw(e.target.value)} className="mt-1.5 rounded-sm border-slate-300 min-h-[140px]" data-testid="source-content" />
            </div>
          </>
        ) : (
          <div>
            <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Document (PDF / DOCX / TXT)</Label>
            <input type="file" accept=".pdf,.docx,.txt,.md" onChange={(e) => setFile(e.target.files[0])} className="mt-1.5 block w-full text-sm text-slate-600 file:mr-3 file:rounded-sm file:border file:border-slate-300 file:bg-slate-50 file:px-3 file:py-1.5" data-testid="source-file" />
          </div>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Checkbox checked={founder} onCheckedChange={setFounder} data-testid="source-founder-authored" /> This source is founder-authored
        </label>
        <Button onClick={submit} disabled={busy} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 gap-2" data-testid="source-submit">
          {tab === "file" ? <Upload size={15} /> : <Plus size={15} />} {busy ? "Importing…" : "Import Source"}
        </Button>
      </div>
    </DialogContent>
  );
}
